import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { UserPlus, Shield, Wrench, Truck, Edit3, KeyRound, Send, Copy, Check } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";

const LANGUAGES = [
  { code: "it", label: "🇮🇹 Italiano" },
  { code: "en", label: "🇬🇧 English" },
  { code: "fr", label: "🇫🇷 Français" },
  { code: "es", label: "🇪🇸 Español" },
];

const roleMeta = {
  admin: { label: "Amministratore", icon: Shield, class: "bg-brand-muted text-brand border-brand/30" },
  tecnico: { label: "Tecnico interno", icon: Wrench, class: "bg-zinc-100 text-zinc-800 border-zinc-200" },
  tecnico_distributore: { label: "Tecnico distributore", icon: Truck, class: "bg-amber-50 text-amber-900 border-amber-200" },
};

const emptyForm = { name: "", email: "", password: "", role: "tecnico", distributor_customer_id: "", phone: "", language: "it" };

function UserFormDialog({ open, onOpenChange, user, customers, onSaved }) {
  const isEdit = !!user?.id;
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(isEdit ? {
        name: user.name || "",
        email: user.email || "",
        password: "",
        role: user.role || "tecnico",
        distributor_customer_id: user.distributor_customer_id || "",
        phone: user.phone || "",
        language: user.language || "it",
      } : emptyForm);
    }
  }, [open, isEdit, user]);

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Nome e email obbligatori"); return; }
    if (!isEdit && !form.password.trim()) { toast.error("Password iniziale obbligatoria"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/users/${user.id}`, {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone || null,
          language: form.language,
          role: form.role,
          distributor_customer_id: form.role === "tecnico_distributore" ? (form.distributor_customer_id || null) : null,
        });
        toast.success("Utente aggiornato");
      } else {
        await api.post("/auth/register", {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          role: form.role,
          phone: form.phone || null,
          language: form.language,
          ...(form.role === "tecnico_distributore" && form.distributor_customer_id ? { distributor_customer_id: form.distributor_customer_id } : {}),
        });
        toast.success("Utente creato");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{isEdit ? "Modifica utente" : "Nuovo utente"}</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            {isEdit ? "Aggiorna dati, ruolo o lingua preferita." : "Crea un account per un tecnico interno o distributore."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Nome</Label>
              <Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Telefono</Label>
              <Input data-testid="user-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-sm" placeholder="+39 ..." />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Email</Label>
            <Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm" />
          </div>
          {!isEdit && (
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Password iniziale</Label>
              <Input data-testid="user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-sm" placeholder="Al primo accesso il tecnico dovrà cambiarla" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Ruolo</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, distributor_customer_id: "" })}>
                <SelectTrigger data-testid="user-role" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Amministratore</SelectItem>
                  <SelectItem value="tecnico">Tecnico interno</SelectItem>
                  <SelectItem value="tecnico_distributore">Tecnico distributore</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Lingua</Label>
              <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                <SelectTrigger data-testid="user-language" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.role === "tecnico_distributore" && (
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Distributore associato</Label>
              <Select value={form.distributor_customer_id || "none"} onValueChange={(v) => setForm({ ...form, distributor_customer_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="user-distributor" className="mt-1 rounded-sm"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Nessuno —</SelectItem>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid="user-save" className="bg-brand hover:bg-brand-dark text-white rounded-sm" onClick={save} disabled={saving}>{saving ? "Salvo..." : (isEdit ? "Aggiorna" : "Crea utente")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ open, onOpenChange, user, onDone }) {
  const [pwd, setPwd] = useState("");
  const submit = async () => {
    if (!pwd || pwd.length < 6) { toast.error("Password minimo 6 caratteri"); return; }
    try {
      await api.post(`/users/${user.id}/reset-password`, { new_password: pwd });
      toast.success("Password reimpostata — il tecnico dovrà cambiarla al primo accesso");
      onDone?.(pwd);
      onOpenChange(false);
      setPwd("");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-brand" /> Reset password · {user?.name}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Imposta una password temporanea. Al primo login il tecnico dovrà cambiarla obbligatoriamente.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Nuova password temporanea</Label>
          <Input data-testid="reset-pwd-input" value={pwd} onChange={(e) => setPwd(e.target.value)} className="mt-1 rounded-sm" placeholder="Minimo 6 caratteri" />
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid="reset-pwd-submit" className="bg-brand hover:bg-brand-dark text-white rounded-sm" onClick={submit}>Reimposta</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InviteDialog({ open, onOpenChange, user, tempPassword }) {
  const [copied, setCopied] = useState(false);
  const url = window.location.origin;
  const inviteText = `🍷 Wineemotion Assistenza\n\nBenvenuto ${user?.name || ""}.\n\nApp: ${url}\nEmail: ${user?.email || ""}\n${tempPassword ? `Password temporanea: ${tempPassword}\n` : ""}\nAl primo accesso ti verrà chiesto di impostare una password personale.\n\nSuggerimento: installa l'app come PWA (menu Condividi → Aggiungi a Home su iPhone; banner "Installa app" su Android).`;

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(inviteText);
      setCopied(true);
      toast.success("Invito copiato — incolla in WhatsApp o email");
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error("Copia non riuscita");
    }
  };

  const openWhatsApp = () => {
    const phone = (user?.phone || "").replace(/[^\d+]/g, "");
    const encoded = encodeURIComponent(inviteText);
    const link = phone ? `https://wa.me/${phone.replace("+", "")}?text=${encoded}` : `https://wa.me/?text=${encoded}`;
    window.open(link, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <Send className="h-5 w-5 text-brand" /> Invita {user?.name}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Condividi il link e le credenziali con il tecnico. Puoi copiare il testo o inviarlo su WhatsApp.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-center bg-white border border-zinc-200 rounded-sm p-4">
            <QRCodeCanvas value={url} size={160} bgColor="#ffffff" fgColor="#722F37" />
          </div>
          <div className="text-center text-[10px] uppercase tracking-widest text-zinc-500 font-mono break-all">{url}</div>
          <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-3 text-xs whitespace-pre-wrap font-mono text-zinc-800 max-h-40 overflow-y-auto" data-testid="invite-text">
            {inviteText}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-sm" onClick={copyText} data-testid="invite-copy-btn">
            {copied ? <><Check className="h-4 w-4 mr-2" /> Copiato</> : <><Copy className="h-4 w-4 mr-2" /> Copia invito</>}
          </Button>
          <Button className="bg-brand hover:bg-brand-dark text-white rounded-sm" onClick={openWhatsApp} data-testid="invite-whatsapp-btn">
            <Send className="h-4 w-4 mr-2" /> WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Utenti() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);

  const load = useCallback(async () => {
    try {
      const [u, c] = await Promise.all([api.get("/users"), api.get("/customers")]);
      setItems(u.data);
      setCustomers(c.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const customerName = (id) => customers.find((c) => c.id === id)?.name || "—";

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (u) => { setEditing(u); setFormOpen(true); };
  const openReset = (u) => { setTarget(u); setResetOpen(true); };
  const openInvite = (u, pwd = null) => { setTarget(u); setTempPassword(pwd); setInviteOpen(true); };

  return (
    <div className="p-4 sm:p-8 max-w-6xl">
      <PageHeader
        title="Tecnici & Distributori"
        description="Gestione account. I distributori vedono solo i propri ticket e ricevono i suggerimenti AI nella loro lingua."
        actions={
          <Button data-testid="new-user-btn" onClick={openNew} className="bg-brand hover:bg-brand-dark text-white rounded-sm">
            <UserPlus className="h-4 w-4 mr-2" /> Nuovo utente
          </Button>
        }
      />
      <Card className="rounded-sm border-zinc-200 p-0 overflow-hidden bg-white">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
              <th className="text-left px-5 py-3 font-medium">Nome</th>
              <th className="text-left px-5 py-3 font-medium">Contatti</th>
              <th className="text-left px-5 py-3 font-medium">Ruolo</th>
              <th className="text-left px-5 py-3 font-medium">Lingua</th>
              <th className="text-left px-5 py-3 font-medium">Distributore</th>
              <th className="text-right px-5 py-3 font-medium">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => {
              const meta = roleMeta[u.role] || roleMeta.tecnico;
              const RoleIcon = meta.icon;
              const langLabel = LANGUAGES.find((l) => l.code === (u.language || "it"))?.label || "🇮🇹 Italiano";
              return (
                <tr key={u.id} className="border-b border-zinc-100" data-testid={`user-row-${u.id}`}>
                  <td className="px-5 py-3 text-zinc-900 font-medium">{u.name}</td>
                  <td className="px-5 py-3 text-zinc-700">
                    <div>{u.email}</div>
                    {u.phone && <div className="text-xs text-zinc-500">{u.phone}</div>}
                  </td>
                  <td className="px-5 py-3">
                    <Badge className={`rounded-sm text-[10px] uppercase tracking-widest border ${meta.class} flex items-center gap-1 w-fit`}>
                      <RoleIcon className="h-3 w-3" /> {meta.label}
                    </Badge>
                  </td>
                  <td className="px-5 py-3 text-xs">{langLabel}</td>
                  <td className="px-5 py-3 text-xs text-zinc-600">
                    {u.role === "tecnico_distributore" && u.distributor_customer_id ? customerName(u.distributor_customer_id) : "—"}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button data-testid={`user-invite-${u.id}`} onClick={() => openInvite(u)} title="Invia invito" className="h-8 w-8 rounded-sm border border-zinc-300 text-zinc-700 hover:bg-brand-muted hover:text-brand flex items-center justify-center">
                        <Send className="h-3.5 w-3.5" />
                      </button>
                      <button data-testid={`user-edit-${u.id}`} onClick={() => openEdit(u)} title="Modifica" className="h-8 w-8 rounded-sm border border-zinc-300 text-zinc-700 hover:bg-zinc-50 flex items-center justify-center">
                        <Edit3 className="h-3.5 w-3.5" />
                      </button>
                      <button data-testid={`user-reset-${u.id}`} onClick={() => openReset(u)} title="Reset password" className="h-8 w-8 rounded-sm border border-zinc-300 text-zinc-700 hover:bg-zinc-50 flex items-center justify-center">
                        <KeyRound className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </Card>

      <UserFormDialog open={formOpen} onOpenChange={setFormOpen} user={editing} customers={customers} onSaved={load} />
      <ResetPasswordDialog open={resetOpen} onOpenChange={setResetOpen} user={target} onDone={(pwd) => { openInvite(target, pwd); load(); }} />
      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} user={target} tempPassword={tempPassword} />
    </div>
  );
}
